import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useI18n } from './I18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'app_currency';

const FALLBACK_RATES = { NOK: 1.0, USD: 0.094, GBP: 0.075, EUR: 0.087 };
const SYMBOLS = { NOK: 'NOK', USD: '$', GBP: '£', EUR: '€' };
const LOCALES = { NOK: 'nb-NO', USD: 'en-US', GBP: 'en-GB', EUR: 'de-DE' };

// Auto-pick currency from language (used until user explicitly overrides)
const langDefaultCurrency = (lang) => (lang === 'no' ? 'NOK' : 'GBP');

/**
 * Returns:
 *  - currency: active currency code
 *  - setCurrency: set manually
 *  - format(amount): formatted string in active currency (amount is in NOK)
 *  - rates, supported
 */
export const useCurrency = () => {
  const { lang } = useI18n();
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [supported, setSupported] = useState(['NOK', 'USD', 'GBP', 'EUR']);
  const [defaultCurrency, setDefaultCurrency] = useState(null); // admin-defined default
  const [override, setOverride] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });

  useEffect(() => {
    axios.get(`${API}/currency/rates`)
      .then(r => {
        setRates(r.data?.rates || FALLBACK_RATES);
        setSupported(r.data?.supported || ['NOK', 'USD', 'GBP', 'EUR']);
      })
      .catch(() => { /* keep fallbacks */ });
    axios.get(`${API}/settings/public`)
      .then(r => setDefaultCurrency(r.data?.default_currency || null))
      .catch((err) => { console.warn('Currency rate fetch failed:', err?.message); });
  }, []);

  const currency = override || defaultCurrency || langDefaultCurrency(lang);

  const setCurrency = useCallback((code) => {
    setOverride(code);
    try { localStorage.setItem(STORAGE_KEY, code); }
    catch (err) { console.warn('localStorage setItem failed:', err?.message); }
  }, []);

  const clearOverride = useCallback(() => {
    setOverride(null);
    try { localStorage.removeItem(STORAGE_KEY); }
    catch (err) { console.warn('localStorage removeItem failed:', err?.message); }
  }, []);

  const format = useCallback((nokAmount) => {
    const amount = (Number(nokAmount) || 0) * (rates[currency] || 1);
    try {
      const formatter = new Intl.NumberFormat(LOCALES[currency] || 'en-US', {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: amount >= 100 ? 0 : 2,
      });
      return formatter.format(amount);
    } catch {
      return `${SYMBOLS[currency] || currency} ${Math.round(amount).toLocaleString()}`;
    }
  }, [currency, rates]);

  return { currency, setCurrency, clearOverride, format, rates, supported, defaultCurrency };
};
